import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiClient } from '@/services/api'
import { notificationsApi } from '@/lib/api'
import { normalizeCounts, notificationCategory } from '@/lib/notificationModel'
import type { NotificationCategory, NotificationCounts } from '@/types/notifications'

// `| undefined` on the optional fields: the live-notification payload builds these with
// possibly-undefined values (exactOptionalPropertyTypes). `entityType` is any string the producers
// send ('deal', 'contact', 'company', 'outreach_reply', 'wa_conversation', 'organization', ...);
// lib/notificationRoute.ts decides what each one opens.
export interface PersistentNotification {
  _id: string
  type: string
  title: string
  body: string
  activityId?: string | undefined
  activityType?: string | undefined
  dueAt?: string | undefined
  entityType?: string | undefined
  entityId?: string | undefined
  entityName?: string | undefined
  read: boolean
  readAt?: string | undefined
  createdAt: string
}

export const useNotificationCenterStore = defineStore('notificationCenter', () => {
  // Dropdown state
  const dropdownNotifications = ref<PersistentNotification[]>([])
  const isDropdownOpen = ref(false)

  // Full-page state
  const pageNotifications = ref<PersistentNotification[]>([])
  const hasMore = ref(false)
  const currentPage = ref(1)
  /** The last page fetch failed (the Notifications page shows a retry instead of "all caught up"). */
  const pageError = ref(false)
  /** Totals for the Notifications page filters; null until loaded, or when the request failed. */
  const counts = ref<NotificationCounts | null>(null)
  // Which page request is the latest: an answer to an older one (a filter changed under it, or
  // another organization was switched to) is dropped instead of landing in the new list.
  let pageRequest = 0

  // Shared state
  const unreadCount = ref(0)
  const isLoading = ref(false)

  async function fetchUnreadCount() {
    try {
      const response = await apiClient.get('/notifications/unread-count')
      unreadCount.value = response.data.unreadCount
    } catch {
      // Non-critical
    }
  }

  async function fetchRecent(limit = 7) {
    isLoading.value = true
    try {
      const response = await apiClient.get('/notifications', {
        params: { page: 1, limit },
      })
      dropdownNotifications.value = response.data.data
    } catch {
      // Non-critical
    } finally {
      isLoading.value = false
    }
  }

  async function fetchPage(page = 1, limit = 20, category?: NotificationCategory) {
    const request = ++pageRequest
    isLoading.value = true
    pageError.value = false
    try {
      const response = await apiClient.get('/notifications', {
        params: { page, limit, ...(category ? { category } : {}) },
      })
      if (request !== pageRequest) return
      if (page === 1) {
        pageNotifications.value = response.data.data
      } else {
        pageNotifications.value.push(...response.data.data)
      }
      hasMore.value = response.data.hasMore
      currentPage.value = page
    } catch {
      // Non-critical for the bell; the page offers a retry
      if (request === pageRequest) pageError.value = true
    } finally {
      if (request === pageRequest) isLoading.value = false
    }
  }

  /** Load the filter pills' totals (all, unread and one per category). Null on failure: no pills. */
  async function fetchCounts() {
    try {
      const response = await notificationsApi.counts()
      counts.value = normalizeCounts(response.data)
    } catch {
      counts.value = null
    }
  }

  async function markAsRead(notificationId: string) {
    // Optimistic update in both arrays. The same notification can sit in both (the bell's list
    // and the page's), so the unread count drops once, not once per list.
    let wasUnread = false
    const updateRead = (list: PersistentNotification[]) => {
      const item = list.find(n => n._id === notificationId)
      if (item && !item.read) {
        item.read = true
        item.readAt = new Date().toISOString()
        wasUnread = true
      }
    }
    updateRead(dropdownNotifications.value)
    updateRead(pageNotifications.value)
    if (wasUnread) {
      unreadCount.value = Math.max(0, unreadCount.value - 1)
      if (counts.value) counts.value.unread = Math.max(0, counts.value.unread - 1)
    }

    try {
      await apiClient.patch(`/notifications/${notificationId}/read`)
    } catch {
      // Already updated optimistically
    }
  }

  async function markAllAsRead() {
    // Optimistic update
    const markAll = (list: PersistentNotification[]) => {
      const now = new Date().toISOString()
      list.forEach(n => {
        if (!n.read) {
          n.read = true
          n.readAt = now
        }
      })
    }
    markAll(dropdownNotifications.value)
    markAll(pageNotifications.value)
    unreadCount.value = 0
    if (counts.value) counts.value.unread = 0

    try {
      await apiClient.patch('/notifications/read-all')
    } catch {
      // Already updated optimistically, re-fetch to sync
      await fetchUnreadCount()
    }
  }

  async function deleteNotification(notificationId: string) {
    // Check if it was unread before removing
    const wasUnread = (list: PersistentNotification[]) => {
      const item = list.find(n => n._id === notificationId)
      return item && !item.read
    }
    const shouldDecrementCount = wasUnread(dropdownNotifications.value) || wasUnread(pageNotifications.value)

    // The page's filter totals lose this notification from All and from its category
    const removed =
      pageNotifications.value.find(n => n._id === notificationId) ??
      dropdownNotifications.value.find(n => n._id === notificationId)
    if (removed && counts.value) {
      const category = notificationCategory(removed.type)
      counts.value.all = Math.max(0, counts.value.all - 1)
      counts.value[category] = Math.max(0, counts.value[category] - 1)
      if (!removed.read) counts.value.unread = Math.max(0, counts.value.unread - 1)
    }

    // Remove from both arrays
    dropdownNotifications.value = dropdownNotifications.value.filter(n => n._id !== notificationId)
    pageNotifications.value = pageNotifications.value.filter(n => n._id !== notificationId)

    if (shouldDecrementCount) {
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }

    try {
      await apiClient.delete(`/notifications/${notificationId}`)
    } catch {
      // Already removed optimistically
    }
  }

  function addLiveNotification(payload: {
    notificationId?: string
    type: string
    title: string
    body: string
    activityId?: string
    activityType?: string
    dueAt?: string
    entityType?: string
    entityId?: string
    entityName?: string
    unreadCount?: number
  }) {
    // Update unread count from server-provided value (authoritative)
    if (payload.unreadCount !== undefined) {
      unreadCount.value = payload.unreadCount
    }

    // Build a PersistentNotification from the socket payload
    if (payload.notificationId) {
      const notification: PersistentNotification = {
        _id: payload.notificationId,
        type: payload.type,
        title: payload.title,
        body: payload.body,
        activityId: payload.activityId,
        activityType: payload.activityType,
        dueAt: payload.dueAt,
        entityType: payload.entityType,
        entityId: payload.entityId,
        entityName: payload.entityName,
        read: false,
        createdAt: new Date().toISOString(),
      }

      // Prepend to dropdown if it's open
      if (isDropdownOpen.value) {
        dropdownNotifications.value.unshift(notification)
        // Keep dropdown at max 7
        if (dropdownNotifications.value.length > 7) {
          dropdownNotifications.value.pop()
        }
      }
    }
  }

  function openDropdown() {
    isDropdownOpen.value = true
    fetchRecent()
  }

  function closeDropdown() {
    isDropdownOpen.value = false
  }

  function resetPageState() {
    pageNotifications.value = []
    hasMore.value = false
    currentPage.value = 1
    pageError.value = false
  }

  return {
    dropdownNotifications,
    isDropdownOpen,
    pageNotifications,
    hasMore,
    currentPage,
    pageError,
    counts,
    unreadCount,
    isLoading,
    fetchUnreadCount,
    fetchRecent,
    fetchPage,
    fetchCounts,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addLiveNotification,
    openDropdown,
    closeDropdown,
    resetPageState,
  }
})
